import { guidFor } from '@ember/object/internals';
import { getOwner } from '@ember/owner';
import Service, { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

import type { FieldSchema } from '@warp-drive/core/types/schema/fields';
import { Copyable } from 'ember-copy';

import { getRecordSettings } from 'umg-ember-backend/utils/get-record-settings';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

type RecordCopyOptions = {
  ignoreAttributes: string[];
  otherAttributes: string[];
  overwrite: Record<string, unknown>;
  relationships: Record<string, Partial<RecordCopyOptions>>;
  relationsToCopy: string[];
  skipRecord: boolean;
};

type RelationshipKind = 'belongsTo' | 'hasMany';

type RelationshipMeta = Extract<FieldSchema, { kind: RelationshipKind }>;

type RelationshipDataKey = `${RelationshipKind}Relationship`;

type RelationshipReference = Record<
  RelationshipDataKey,
  {
    addRecordDatas(members: unknown): void;
    members: unknown;
  }
>;

type CopyableValue = {
  copy(deep: boolean): unknown;
};

type CopyRecord = {
  [key: string]: TODO;
};

type Transform = {
  deserialize(value: unknown, options?: Record<string, unknown>): unknown;
  serialize(value: unknown, options?: Record<string, unknown>): unknown;
};

type CopyMeta = {
  copies: Record<string, CopyRecord>;
  transforms: Record<string, Transform>;
};

type RecordCopyStore = {
  createRecord(recordType: string): unknown;
  schema: {
    fields(resource: { type: string }): Map<string, FieldSchema>;
  };
  unloadRecord(record: CopyRecord): void;
};

type Relationship = {
  meta: RelationshipMeta;
  name: string;
};

const PRIMITIVE_TYPES = ['string', 'number', 'boolean'] as const;
const GLOBAL_IGNORE_ATTRIBUTES = [
  'cmsHistory',
  'cmsCreated',
  'cmsDeleted',
  'cmsLocktime',
  'cmsModified',
  'cmsOwnerId',
  'cmsCreatorId',
  'cmsLockerId',
  'cmsModifierId',
  'cmsVariantParentId',
];

const DEFAULT_OPTIONS: RecordCopyOptions = {
  // List of all attributes to ignore
  ignoreAttributes: [],

  // List of other attributes to copy
  otherAttributes: [],

  // Overwrite specific keys with a given value
  overwrite: {},

  // Relationship options
  relationships: {},

  // List of all attributes to copy by reference
  relationsToCopy: [],

  // Flag to skip this record
  skipRecord: false,
};

function isCopyableValue(value: unknown): value is CopyableValue {
  return Copyable.detect(value);
}

export default class RecordCopyService extends Service {
  // Services
  @service declare store: RecordCopyStore;

  runningCopies = new WeakMap<CopyRecord, Promise<CopyRecord | null>>();

  copy(
    record: CopyRecord,
    options: Partial<RecordCopyOptions> = {},
  ): Promise<CopyRecord | null> {
    if (this.runningCopies.has(record)) {
      return this.runningCopies.get(record)!;
    }

    const promise = this.performCopy(record, options);
    this.runningCopies.set(record, promise);

    return promise.finally(() => {
      this.runningCopies.delete(record);
    });
  }

  async performCopy(
    record: CopyRecord,
    options: Partial<RecordCopyOptions>,
  ): Promise<CopyRecord | null> {
    const meta: CopyMeta = { copies: {}, transforms: {} };
    let isSuccessful = false;

    try {
      const copy = await this.copyRecordWithMeta(record, options, meta);
      isSuccessful = true;

      return copy;
    } catch (error) {
      throw new Error(String(error));
    } finally {
      if (!isSuccessful) {
        // Unload all created records
        Object.values(meta.copies).forEach((record) => {
          this.store.unloadRecord(record);
        });
      }
    }
  }

  async copyRecordWithMeta(
    record: CopyRecord,
    options: Partial<RecordCopyOptions>,
    meta: CopyMeta,
  ): Promise<CopyRecord | null> {
    const guid = guidFor(record);
    const attrs: Record<string, unknown> = {};

    const copyOptions: RecordCopyOptions = {
      ...DEFAULT_OPTIONS,
      ...getRecordSettings<Partial<RecordCopyOptions>>(
        record,
        'copyableOptions',
      ),
      ...options,
    };
    copyOptions.ignoreAttributes = [
      ...GLOBAL_IGNORE_ATTRIBUTES,
      ...copyOptions.ignoreAttributes,
    ];

    // If the model has already been copied, just return that model
    if (meta.copies[guid]) {
      return meta.copies[guid];
    }

    // Skip the model if model options skipRecord is set
    if (copyOptions.skipRecord) {
      return null;
    }

    // Create a new record and assign it to meta.copies
    const copy = this.store.createRecord(getRecordType(record)) as CopyRecord;
    meta.copies[guid] = copy;

    // Copy all the attributes
    this.copyAttributes(record, attrs, copyOptions, meta);

    // Copy all the relationships
    await this.copyRelations(record, copy, attrs, copyOptions, meta);

    // Copy all the other attributes
    copyOptions.otherAttributes.forEach((name) => {
      attrs[name] = record[name];
    });

    Object.assign(copy, attrs);

    return copy;
  }

  copyAttributes(
    record: CopyRecord,
    attrs: Record<string, unknown>,
    options: RecordCopyOptions,
    meta: CopyMeta,
  ): Record<string, unknown> {
    this.store.schema
      .fields({ type: getRecordType(record) })
      .forEach((field, name) => {
        if (field.kind !== 'attribute') {
          return;
        }

        const { type, options: attributeOptions } = field;

        if (options.ignoreAttributes.includes(name)) {
          // Ignore this attribute if it's defined under ignoreAttributes
          return;
        } else if (name in options.overwrite) {
          // Overwrite this attribute if it's defined under overwrite
          attrs[name] = options.overwrite[name];
          return;
        } else if (
          !isEmpty(type) &&
          !PRIMITIVE_TYPES.includes(type as (typeof PRIMITIVE_TYPES)[number])
        ) {
          let value: unknown = record[name];

          if (isCopyableValue(value)) {
            // Copy attribute with an EmberObject
            value = value.copy(true);
          } else {
            // Copy attribute with a custom transform
            let transform = meta.transforms[type!];

            if (!transform) {
              transform = getOwner(this)!.lookup(
                `transform:${type}`,
              ) as Transform;
              meta.transforms[type!] = transform;
            }

            value = transform.serialize(value, attributeOptions);
            value = transform.deserialize(value, attributeOptions);
          }

          attrs[name] = value;
        } else {
          // Copy attribute with primitive transform
          attrs[name] = record[name];
        }
      });

    return attrs;
  }

  async copyRelations(
    record: CopyRecord,
    copy: CopyRecord,
    attrs: Record<string, unknown>,
    options: RecordCopyOptions,
    meta: CopyMeta,
  ): Promise<void> {
    const relationships: Relationship[] = [];

    this.store.schema
      .fields({ type: getRecordType(record) })
      .forEach((field, name) => {
        if (
          (field.kind === 'belongsTo' || field.kind === 'hasMany') &&
          !options.ignoreAttributes.includes(name)
        ) {
          relationships.push({
            name,
            meta: field,
          });
        }
      });

    // Copy all the relationships
    for (const { name, meta: relationshipMeta } of relationships) {
      const relOptions = options.relationships[name] ?? {};

      if (name in options.overwrite) {
        attrs[name] = options.overwrite[name];
        continue;
      }

      // If it's not a deep copy, we just copied by reference
      if (!options.relationsToCopy.includes(name)) {
        try {
          const relationshipKey: RelationshipDataKey = `${relationshipMeta.kind}Relationship`;
          const ref = record[relationshipMeta.kind](
            name,
          ) as RelationshipReference;
          const copyRef = copy[relationshipMeta.kind](
            name,
          ) as RelationshipReference;

          copyRef[relationshipKey].addRecordDatas(ref[relationshipKey].members);
        } catch {
          attrs[name] = await record[name];
        }

        continue;
      }

      const relation = (await record[name]) as CopyRecord | CopyRecord[] | null;

      if (!relation) {
        continue;
      }

      // Create a new deep copy of the relationship
      if (relationshipMeta.kind === 'belongsTo') {
        attrs[name] = await this.copyRecordWithMeta(relation, relOptions, meta);
      } else if (relationshipMeta.kind === 'hasMany') {
        const relationRecords = relation as CopyRecord[];
        const copiedRelations = await Promise.all(
          relationRecords.map((item) =>
            this.copyRecordWithMeta(item, relOptions, meta),
          ),
        );

        // Remove skipped records
        attrs[name] = copiedRelations.filter(Boolean);
      }
    }
  }
}
